import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Text } from '@/components/gluestack/text';
import { SettingsToggle } from '@/components/ui/SettingsToggle';
import { useAccountSettings, useUpdateAccountSetting } from '@/hooks/useAccountSettings';
import { invalidateFilteredListQueries } from '@/hooks/useQueryCacheUtils';
import { EyeIcon } from '@hugeicons-pro/core-stroke-rounded';
import type { ISheetConfig } from '@/components/ui/ContextSheet';

export default function SafetySettingsContent() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const safeModeChanged = useRef(false);
  const { data: accountSettings, isPending: isLoading } = useAccountSettings();
  const { updateSetting } = useUpdateAccountSetting();

  React.useEffect(() => {
    return () => {
      if (safeModeChanged.current) {
        invalidateFilteredListQueries(queryClient);
      }
    };
  }, [queryClient]);

  if (isLoading) {
    return <Text className="text-typography-500">{t('common.loading')}</Text>;
  }

  if (!accountSettings) return null;

  return (
    <SettingsToggle
      icon={EyeIcon}
      label={t('settings.preferences.safeModeOff')}
      description={t('settings.preferences.safeModeOffDescription')}
      value={accountSettings.SafeModeOff}
      onValueChange={(value) =>
        updateSetting('SafeModeOff', value, {
          onSuccess: () => {
            safeModeChanged.current = true;
          },
        })
      }
    />
  );
}

SafetySettingsContent.sheetConfig = {
  largeScreenMode: 'modal',
  height: 'xs',
} satisfies ISheetConfig;
